'use client';

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';

import {
  usePaymentFlow,
  usePaymentFlowWithProcessors,
  useUpdatePaymentFlow,
  PaymentFlowDetails,
  UpdatePaymentFlowInput,
} from '@/lib/api/paymentFlows';
import { ErrorDisplay } from '@/components/ui/error-display';
import { parseTRPCError } from '@/lib/utils/trpc-error-utils';
import { PaymentFlowEditSkeleton } from '@/components/form/payment-flow/payment-flow-edit-skeleton';
import {
  PaymentFlowForm,
  PaymentFlowFormData,
} from '@/components/form/payment-flow/payment-flow-form';
import { routingForSubmit, routingFromConfig } from '@/components/form/payment-flow/processor-routing';
import { Button } from '@/components/ui/button';
import { useOrgNavigate } from '@/hooks/use-app-navigate';

/**
 * Org-level page for editing a payment flow
 */
const EditPaymentFlowPageInner = () => {
  const { flowId } = useParams();
  const { t } = useTranslation('paymentFlows');

  const {
    data: paymentFlow,
    isLoading,
    error,
  } = usePaymentFlow(flowId || '', !!flowId);

  if (!flowId) {
    return null;
  }

  if (isLoading) {
    return <PaymentFlowEditSkeleton />;
  }

  if (error) {
    return <ErrorDisplay error={parseTRPCError(error)} />;
  }

  if (!paymentFlow) {
    return (
      <div className="container mx-auto py-8">
        <p>{t('paymentFlowNotFound')}</p>
      </div>
    );
  }

  return <EditPaymentFlowContent paymentFlow={paymentFlow} />;
};

/**
 * Form component for editing payment flow
 */
const EditPaymentFlowContent = ({
  paymentFlow,
}: {
  paymentFlow: NonNullable<PaymentFlowDetails>;
}) => {
  const { orgRoutes, navigate } = useOrgNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl');
  const updatePaymentFlow = useUpdatePaymentFlow();
  // Capability is computed server-side on the saved flow and refreshed by the update mutation.
  const { data: paymentFlowWithProcessors } = usePaymentFlowWithProcessors(paymentFlow.id);
  const { t } = useTranslation(['paymentFlows', 'common']);

  const initialData: PaymentFlowFormData = {
    name: paymentFlow.name || '',
    pickProcessorStrategy: (paymentFlow.pickProcessorStrategy as 'weighted' | 'automatic') || 'weighted',
    fallbackMode: paymentFlow.fallbackMode ?? false,
    maxFallbackRetries: paymentFlow.maxFallbackRetries ?? 2,
    threeDsEnabled: paymentFlow.threeDsEnabled ?? false,
    stickyProcessorEnabled: paymentFlow.stickyProcessorEnabled ?? false,
    processorConfigs:
      paymentFlow.processorConfigs?.map((config) => ({
        processorId: config.processorId || '',
        weight: config.weight ?? 0,
        disabled: config.disabled ?? false,
        nonStickable: config.nonStickable ?? false,
        disabledCardBrands: config.disabledCardBrands ?? [],
        routing: routingFromConfig(config),
      })) || [],
    fallbackProcessorConfigs:
      paymentFlow.fallbackProcessorConfigs?.map((config, index) => ({
        processorId: config.processorId || '',
        orderIndex: config.orderIndex ?? index,
      })) || [],
  };

  const handleSubmit = async (data: PaymentFlowFormData) => {
    const updateData = {
      flowId: paymentFlow.id,
      data: {
        name: data.name,
        pickProcessorStrategy: data.pickProcessorStrategy,
        fallbackMode: data.fallbackMode,
        maxFallbackRetries: data.maxFallbackRetries,
        threeDsEnabled: data.threeDsEnabled,
        stickyProcessorEnabled: data.stickyProcessorEnabled,
        processorConfigs: data.processorConfigs.map((config) => ({
          processorId: config.processorId,
          weight: config.weight,
          disabled: config.disabled,
          nonStickable: config.nonStickable,
          disabledCardBrands: config.disabledCardBrands,
          routing: routingForSubmit(config.routing),
        })),
        fallbackProcessorConfigs: data.fallbackProcessorConfigs.map((config) => ({
          processorId: config.processorId,
          orderIndex: config.orderIndex,
        })),
      },
    };

    await updatePaymentFlow.mutateAsync(updateData as UpdatePaymentFlowInput);
    if (returnUrl) {
      window.location.href = decodeURIComponent(returnUrl);
    } else {
      navigate(orgRoutes.PAYMENT_FLOW_DETAILS(paymentFlow.id));
    }
  };

  const handleCancel = () => {
    if (returnUrl) {
      window.location.href = decodeURIComponent(returnUrl);
    } else {
      navigate(orgRoutes.PAYMENT_FLOW_DETAILS(paymentFlow.id));
    }
  };

  return (
    <PaymentFlowForm
      Header={({ isPending, onCancel }) => (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={onCancel} disabled={isPending}>
              <ArrowLeft className="size-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold sm:text-3xl">
                {t('paymentFlows:edit.title')}
              </h1>
              <p className="text-muted-foreground text-sm">
                {t('paymentFlows:edit.subtitle', { name: paymentFlow.name })}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isPending}
            >
              {t('common:cancel')}
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending
                ? t('common:saving')
                : t('common:save')}
            </Button>
          </div>
        </div>
      )}
      initialData={initialData}
      hasStandalone3dsCapableProcessor={paymentFlowWithProcessors?.hasStandalone3dsCapableProcessor}
      isPending={updatePaymentFlow.isPending}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
    />
  );
};

export const EditPaymentFlowPage = withRequiredPermission(EditPaymentFlowPageInner, PERMISSIONS.PROCESSORS_MANAGE);
export default EditPaymentFlowPage;


